import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Lock, Unlock, ExternalLink } from "lucide-react";

export default function AdminSupport() {
  return (
    <div data-testid="admin-support-panel" className="space-y-12">
      <MissionEditor />
      <SponsorableArtworks />
      <SupportersList />
    </div>
  );
}

/* ----- Mission Editor ----- */
function MissionEditor() {
  const [m, setM] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newOutcome, setNewOutcome] = useState("");

  const reload = async () => {
    try {
      const [{ data: mission }, { data: p }] = await Promise.all([
        http.get("/admin/support/mission"),
        http.get("/support/progress"),
      ]);
      setM(mission);
      setProgress(p);
    } catch {
      toast.error("Couldn't load mission.");
    }
  };
  useEffect(() => { reload(); }, []);

  if (!m) return <div className="text-[#8a857d] font-body">Loading…</div>;

  const set = (k, v) => setM({ ...m, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      await http.patch("/admin/support/mission", {
        active: m.active,
        title: m.title?.trim(),
        description: m.description?.trim(),
        goal_cents: Number(m.goal_cents) || 0,
        custom_raised_cents: Number(m.custom_raised_cents) || 0,
      });
      toast.success("Mission saved.");
      reload();
    } catch {
      toast.error("Save failed.");
    } finally { setBusy(false); }
  };

  const saveOutcomes = async (next) => {
    try {
      await http.patch("/admin/support/mission", { outcomes: next });
      setM({ ...m, outcomes: next });
      toast.success("Outcomes updated.");
    } catch {
      toast.error("Couldn't update outcomes.");
    }
  };

  const addOutcome = () => {
    if (!newOutcome.trim()) return;
    saveOutcomes([...(m.outcomes || []), newOutcome.trim()]);
    setNewOutcome("");
  };

  const removeOutcome = (idx) => {
    const next = [...(m.outcomes || [])];
    next.splice(idx, 1);
    saveOutcomes(next);
  };

  const raisedDollars = ((progress?.raised_cents || 0) / 100).toFixed(2);
  const goalDollars = ((m.goal_cents || 0) / 100).toFixed(2);

  return (
    <section data-testid="mission-editor" className="border border-white/10 bg-[#0a0a0a] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="font-display text-xl text-[#F4F1EA]">Current Mission</div>
          <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mt-1">
            ${raisedDollars} raised of ${goalDollars} goal · {progress?.percent?.toFixed(1) || 0}% · {progress?.supporter_count || 0} supporters
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-[#F4F1EA] cursor-pointer">
          <input
            type="checkbox"
            data-testid="mission-active"
            checked={!!m.active}
            onChange={(e) => set("active", e.target.checked)}
            className="accent-[#C9A055]"
          />
          Show progress section publicly
        </label>
      </div>

      <Field label="Mission Title">
        <input
          data-testid="mission-title"
          className="stc-input"
          value={m.title || ""}
          onChange={(e) => set("title", e.target.value)}
          maxLength={120}
        />
      </Field>

      <Field label="Description">
        <textarea
          data-testid="mission-description"
          className="stc-input min-h-[90px]"
          value={m.description || ""}
          onChange={(e) => set("description", e.target.value)}
          maxLength={600}
        />
      </Field>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Goal ($)">
          <input
            data-testid="mission-goal"
            type="number" min="0" step="50"
            className="stc-input"
            value={Math.round((m.goal_cents || 0) / 100)}
            onChange={(e) => set("goal_cents", Math.round((Number(e.target.value) || 0) * 100))}
          />
        </Field>
        <Field label="Off-platform raised ($) — added to live total">
          <input
            data-testid="mission-custom-raised"
            type="number" min="0" step="1"
            className="stc-input"
            value={Math.round((m.custom_raised_cents || 0) / 100)}
            onChange={(e) => set("custom_raised_cents", Math.round((Number(e.target.value) || 0) * 100))}
          />
        </Field>
      </div>

      <div className="mt-2 mb-5">
        <button data-testid="mission-save" onClick={save} disabled={busy} className="btn-gold flex items-center gap-2 disabled:opacity-60">
          <Save size={14} /> {busy ? "Saving…" : "Save Mission"}
        </button>
      </div>

      <div className="border-t border-white/10 pt-5 mt-5">
        <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-3">Outcomes Checklist (shown publicly under progress bar)</div>
        <div className="space-y-1.5 mb-3">
          {(m.outcomes || []).map((o, i) => (
            <div key={i} className="flex items-center gap-2 group" data-testid={`outcome-row-${i}`}>
              <span className="text-[#C9A055]">✓</span>
              <span className="flex-1 font-body text-sm text-[#B5B0A7]">{o}</span>
              <button
                onClick={() => removeOutcome(i)}
                className="opacity-0 group-hover:opacity-100 transition text-red-400 hover:text-red-300"
                data-testid={`outcome-remove-${i}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            data-testid="outcome-new-input"
            className="stc-input flex-1"
            placeholder="Add an outcome (e.g. 25 new pieces created)"
            value={newOutcome}
            onChange={(e) => setNewOutcome(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOutcome(); } }}
            maxLength={120}
          />
          <button onClick={addOutcome} data-testid="outcome-add-btn" className="btn-outline flex items-center gap-1.5">
            <Plus size={13} /> Add
          </button>
        </div>
      </div>
    </section>
  );
}

/* ----- Sponsor-A-Truth toggles ----- */
function SponsorableArtworks() {
  const [arts, setArts] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/artworks", { params: { limit: 200 } });
      setArts(data || []);
    } catch {
      toast.error("Couldn't load artworks.");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const toggle = async (a) => {
    if (a.sponsored_by) {
      toast.error(`Already sponsored by ${a.sponsored_by.name}.`);
      return;
    }
    try {
      const path = a.sponsor_open ? "sponsor-close" : "sponsor-open";
      await http.post(`/admin/support/artworks/${a.id}/${path}`);
      toast.success(a.sponsor_open ? "Closed for sponsorship." : "Now open for sponsorship.");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed.");
    }
  };

  if (loading) return <div className="text-[#8a857d] font-body">Loading artworks…</div>;

  return (
    <section data-testid="sponsorable-artworks" className="border border-white/10 bg-[#0a0a0a] p-6">
      <div className="font-display text-xl text-[#F4F1EA] mb-1">Sponsor-A-Truth</div>
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-5">
        Toggle which artworks supporters can sponsor ($50 min) to be credited in the Truth Registry
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {arts.map((a) => (
          <div key={a.id} data-testid={`sponsor-row-${a.slug || a.id}`}
               className={`border bg-[#050505] p-3 flex items-center gap-3 ${a.sponsored_by ? "border-[#C9A055]/60" : a.sponsor_open ? "border-[#C9A055]/30" : "border-white/10"}`}>
            {a.main_image && (
              <img src={a.main_image} alt={a.title} className="w-14 h-14 object-cover bg-[#0a0a0a] border border-white/10" />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm text-[#F4F1EA] truncate">{a.title}</div>
              {a.sponsored_by ? (
                <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mt-1 flex items-center gap-1">
                  ✦ Sponsored · {a.sponsored_by.name}
                </div>
              ) : a.sponsor_open ? (
                <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mt-1">Open for sponsorship</div>
              ) : (
                <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mt-1">Not open</div>
              )}
            </div>
            <button
              onClick={() => toggle(a)}
              disabled={!!a.sponsored_by}
              data-testid={`sponsor-toggle-${a.slug || a.id}`}
              className={`p-2 border ${a.sponsor_open ? "border-[#C9A055] text-[#C9A055]" : "border-white/15 text-[#8a857d] hover:text-[#F4F1EA]"} disabled:opacity-40`}
              title={a.sponsored_by ? "Already sponsored" : (a.sponsor_open ? "Close" : "Open")}
            >
              {a.sponsor_open ? <Unlock size={14} /> : <Lock size={14} />}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ----- Supporters list ----- */
function SupportersList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    http.get("/admin/support/supporters")
      .then(({ data }) => setRows(data || []))
      .catch(() => toast.error("Couldn't load supporters."))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return null;
  if (!rows.length) {
    return (
      <section className="border border-dashed border-white/10 bg-[#0a0a0a] p-8 text-center text-[#8a857d] font-body text-sm">
        No supporters yet — the wall will populate as soon as your first patron checks out.
      </section>
    );
  }
  return (
    <section data-testid="supporters-table" className="border border-white/10 bg-[#0a0a0a] p-6">
      <div className="font-display text-xl text-[#F4F1EA] mb-1">All Supporters ({rows.length})</div>
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-4">
        Most recent first · includes pending Stripe sessions
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm font-body">
          <thead className="text-left font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] border-b border-white/10">
            <tr>
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 pr-3">Tier</th>
              <th className="py-2 pr-3 text-right">Amount</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Sponsor</th>
              <th className="py-2 pr-3">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-white/5" data-testid={`supporter-${s.id}`}>
                <td className="py-2 pr-3 text-[#F4F1EA]">{s.name}<div className="font-ui text-[10px] text-[#8a857d]">{s.email}</div></td>
                <td className="py-2 pr-3 text-[#C9A055]">{s.tier_name}</td>
                <td className="py-2 pr-3 text-right text-[#F4F1EA]">${s.amount?.toFixed(2)}</td>
                <td className="py-2 pr-3">
                  <span className={s.status === "paid" ? "text-emerald-400" : "text-[#8a857d]"}>{s.status}</span>
                </td>
                <td className="py-2 pr-3">
                  {s.is_sponsor_a_truth ? (
                    <span className="text-[#C9A055]">✦ {s.sponsor_artwork_title}{s.sponsor_city ? ` · ${s.sponsor_city}` : ""}</span>
                  ) : <span className="text-[#8a857d]">—</span>}
                </td>
                <td className="py-2 pr-3 text-[#8a857d] font-ui text-[11px]">{s.paid_at ? new Date(s.paid_at).toLocaleDateString() : new Date(s.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ----- shared ----- */
function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
